/**
 * The Dashboard.
 *
 * The scrapers at a glance, for everyone on the team rather than only for
 * whoever configures sources: what is running right now, how the recent Runs
 * went, which scrapers need somebody, and how much each sport holds. Every
 * number is captioned in plain words, and every row links to the screen that
 * explains it in full — this screen summarises and never edits.
 *
 * The numbers are the server's (`GET /dashboard`). A scraper is *needing
 * attention* exactly when the Sources screen sorts it to the top, so the two
 * screens never disagree. The wording lives in `../scraper-dashboard`, where
 * `tests/scraper-dashboard.test.ts` can assert it.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, type ScraperDashboard as DashboardData, type DashboardDays, type DashboardRun } from "../api";
import { POLL_LIST_MS, POLL_RUN_MS, keys } from "../queries";
import { Layout } from "../components/Layout";
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Label,
  Muted,
  Pager,
  PillGroup,
  Spinner,
  StatTile,
  StatusBadge,
  TableScroll,
  cx,
} from "../components/ui";
import { DashboardIcon, PlusIcon } from "../components/icons";
import { formatDuration, formatRelative } from "../format";
import {
  WINDOWS,
  attentionBreakdown,
  attentionCounts,
  attentionTotal,
  bucketLabel,
  bucketTotal,
  chartCeiling,
  describeActivity,
  formatRate,
  labelledIndexes,
  outcomeSentence,
  rateTone,
  reasonScale,
  reasonTitle,
  scraperPath,
  sourcesForSport,
  sportName,
  stuckSentence,
  triggerLabel,
  viewerTimeZone,
  whenPhrase,
  windowPhrase,
  zoneName,
} from "../scraper-dashboard";
import { PAGE_SIZES, clampPage, pageCount } from "../pagination";

export function ScraperDashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState<DashboardDays>(7);
  const [timeZone] = useState(viewerTimeZone);

  const { data, error } = useQuery({
    queryKey: keys.dashboard(days, timeZone),
    queryFn: () => api.dashboard(days, timeZone),
    // Faster while something is running, so a Run visibly moves through its
    // stages; the ordinary list cadence otherwise.
    refetchInterval: (query) => (query.state.data?.active.length ? POLL_RUN_MS * 2 : POLL_LIST_MS),
  });

  const switcher = (
    <PillGroup
      value={String(days)}
      options={WINDOWS.map((w) => ({ value: String(w.value), label: w.label }))}
      onChange={(value) => setDays(Number(value) as DashboardDays)}
    />
  );

  if (!data) {
    return (
      <Layout breadcrumbs={[{ label: "Dashboard" }]} title="Dashboard">
        {error ? (
          <Banner tone="danger" title="The dashboard could not be loaded.">
            {error.message}
          </Banner>
        ) : (
          <Spinner label="Counting the scrapers" />
        )}
      </Layout>
    );
  }

  const empty = data.scrapers.total === 0 && data.transfer_portal.total === 0;

  return (
    <Layout
      breadcrumbs={[{ label: "Dashboard" }]}
      title="Dashboard"
      description="How every scraper in the workspace is doing, in one place. Updates by itself."
      actions={empty ? undefined : switcher}
    >
      <div className="flex flex-col gap-lg">
        {error && <Banner tone="warning" title="Showing the last numbers loaded — the refresh failed." />}

        {empty ? (
          <EmptyState
            icon={<DashboardIcon size={20} />}
            title="No scrapers yet"
            action={
              <Button variant="primary" icon={<PlusIcon size={16} />} onClick={() => navigate("/sources/new")}>
                Add your first source
              </Button>
            }
          >
            Once a source is added and has run, this page shows how many scrapers there are, what is running,
            and how their runs went.
          </EmptyState>
        ) : (
          <>
            <Headline data={data} />
            <RunsChart data={data} />
            <div className="grid grid-cols-1 gap-lg fold:grid-cols-2">
              <RunningNow runs={data.active} />
              <NeedsAttention data={data} />
            </div>
            <div className="grid grid-cols-1 gap-lg fold:grid-cols-2">
              <ComingUp data={data} />
              <MostCommonFailures data={data} />
            </div>
            <BySport data={data} />
            {data.transfer_portal.total > 0 && <TransferPortalSummary data={data} />}
            <RunHistory />
          </>
        )}
      </div>
    </Layout>
  );
}

// --- Headline numbers ------------------------------------------------------

function Headline({ data }: { data: DashboardData }) {
  const { now, period, scrapers } = data;
  const window = windowPhrase(data.days);

  return (
    <div className="flex flex-col gap-md">
      <div className="grid grid-cols-1 gap-md fold:grid-cols-4">
        <StatTile
          label="Running now"
          value={now.running}
          hint={
            now.queued > 0
              ? `${now.queued} more waiting to start`
              : now.running > 0
                ? "Collecting data at this moment"
                : "Nothing is running right now"
          }
        />
        <StatTile label="Runs finished" value={period.finished.toLocaleString()} hint={`In ${window}`} />
        <StatTile
          label="Success rate"
          value={formatRate(period.success_rate)}
          tone={rateTone(period.success_rate)}
          hint={
            period.success_rate === null
              ? `No runs finished in ${window}`
              : `${period.succeeded} worked, ${period.failed} failed`
          }
        />
        <StatTile
          label="Failed runs"
          value={period.failed}
          tone={period.failed > 0 ? "danger" : "neutral"}
          hint={
            period.average_duration_ms !== null
              ? `A run takes ${formatDuration(period.average_duration_ms)} on average`
              : `In ${window}`
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-md fold:grid-cols-4">
        <StatTile
          label="Scrapers"
          value={scrapers.total}
          hint={`${scrapers.scheduled} run on a schedule, the rest by hand`}
        />
        <StatTile
          label="Needing attention"
          value={scrapers.needs_attention}
          tone={scrapers.needs_attention > 0 ? "danger" : "neutral"}
          hint={
            attentionTotal(attentionCounts(data)) > 0
              ? attentionBreakdown(attentionCounts(data))
              : scrapers.never_run > 0
                ? `Nothing needs attention · ${scrapers.never_run} never run`
                : "Nothing needs attention"
          }
        />
        <StatTile
          label="Sports covered"
          value={scrapers.sports}
          hint={`${scrapers.leagues} league${scrapers.leagues === 1 ? "" : "s"} across them`}
        />
        <StatTile
          label="Players collected"
          value={scrapers.players.toLocaleString()}
          hint="From each scraper's latest published data"
        />
      </div>
    </div>
  );
}

// --- Runs over time --------------------------------------------------------

/**
 * Stacked bars in plain divs rather than an SVG: the bars stretch to whatever
 * width the card has, and the labels stay real text at real sizes.
 */
function RunsChart({ data }: { data: DashboardData }) {
  const ceiling = chartCeiling(data.buckets);
  const labelled = labelledIndexes(data.buckets.length);
  const per = data.bucket === "hour" ? "hour" : "day";

  return (
    <Card
      title="Runs over time"
      description={`How many runs finished each ${per} in ${windowPhrase(data.days)}, in your time (${zoneName(
        data.timezone,
      )}). Test runs are not counted.`}
    >
      <div className="flex gap-sm">
        <div className="flex h-[160px] flex-col justify-between text-right text-caption text-muted-soft">
          <span>{ceiling}</span>
          <span>0</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex h-[160px] items-end gap-[3px] border-b border-hairline">
            {data.buckets.map((bucket) => {
              const total = bucketTotal(bucket);
              const label = bucketLabel(bucket.start, data.bucket, data.timezone);
              return (
                <div
                  key={bucket.start}
                  className="flex h-full min-w-0 flex-1 flex-col justify-end"
                  title={`${label}: ${bucket.succeeded} worked, ${bucket.failed} failed${
                    bucket.other ? `, ${bucket.other} stopped or skipped` : ""
                  }`}
                >
                  {total > 0 && (
                    <div
                      className="flex flex-col overflow-hidden rounded-t-sm"
                      style={{ height: `${(total / ceiling) * 100}%` }}
                    >
                      <div className="bg-surface-strong" style={{ flexGrow: bucket.other }} />
                      <div className="bg-danger-ink" style={{ flexGrow: bucket.failed }} />
                      <div className="bg-success-ink" style={{ flexGrow: bucket.succeeded }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-xxs flex gap-[3px]">
            {data.buckets.map((bucket, index) => (
              <div key={bucket.start} className="min-w-0 flex-1 text-center text-caption text-muted-soft">
                {labelled.has(index) ? bucketLabel(bucket.start, data.bucket, data.timezone) : ""}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-md flex flex-wrap gap-md text-caption text-muted">
        <Legend className="bg-success-ink">Worked</Legend>
        <Legend className="bg-danger-ink">Failed</Legend>
        <Legend className="bg-surface-strong">Stopped or skipped</Legend>
      </div>
    </Card>
  );
}

function Legend({ className, children }: { className: string; children: string }) {
  return (
    <span className="flex items-center gap-xxs">
      <span className={cx("size-[10px] rounded-sm", className)} />
      {children}
    </span>
  );
}

// --- Running and failing ---------------------------------------------------

function RunningNow({ runs }: { runs: DashboardRun[] }) {
  return (
    <Card title="Running right now" description="Scrapers collecting data at this moment, and what each is doing.">
      {runs.length === 0 ? (
        <Muted>Nothing is running. Scheduled scrapers start by themselves at their set time.</Muted>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-sm p-0">
          {runs.map((run) => (
            <li key={run.run_id} className="flex items-start justify-between gap-sm">
              <div className="min-w-0">
                <Link to={`/runs/${run.run_id}`} className="text-body-sm-medium text-ink">
                  {run.name}
                </Link>
                <div className="text-caption text-muted">
                  {describeActivity(run)} · {triggerLabel(run.trigger)} · started{" "}
                  {formatRelative(run.started_at ?? run.created_at).toLowerCase()}
                </div>
                {run.stuck && <div className="mt-xxs text-caption text-warning-ink">{stuckSentence(run)}</div>}
              </div>
              {run.stuck ? <Badge tone="warning">Looks stuck</Badge> : <StatusBadge status={run.status} />}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Everything somebody should look at, grouped by what is wrong, each with what
 * to do about it. A failed run is the problem already happened; a missed
 * schedule, a broken sign-in and a stuck run are problems that will keep the
 * data from arriving if nobody steps in.
 */
function NeedsAttention({ data }: { data: DashboardData }) {
  const stuck = data.active.filter((run) => run.stuck);
  const total = attentionTotal(attentionCounts(data));

  return (
    <Card title="Needs attention" description="Anything that needs someone to look at it, and what to do.">
      {total === 0 ? (
        <Muted>All good — every scraper's last run worked and nothing is overdue.</Muted>
      ) : (
        <div className="flex flex-col gap-lg">
          {data.attention.length > 0 && (
            <AttentionGroup title="Last run failed">
              {data.attention.map((run) => (
                <li key={run.run_id} className="min-w-0">
                  <div className="flex items-center justify-between gap-sm">
                    <Link to={scraperPath(run)} className="text-body-sm-medium text-ink">
                      {run.name}
                    </Link>
                    <Link to={`/runs/${run.run_id}`} className="shrink-0 text-caption">
                      {formatRelative(run.finished_at ?? run.created_at)}
                    </Link>
                  </div>
                  <div className="mt-xxs text-body-sm text-danger-ink">{outcomeSentence(run)}</div>
                  {run.failure_hint && <div className="mt-xxs text-caption text-muted">{run.failure_hint}</div>}
                </li>
              ))}
            </AttentionGroup>
          )}

          {data.missed_schedules.length > 0 && (
            <AttentionGroup title="Missed its schedule">
              {data.missed_schedules.map((missed) => (
                <li key={missed.profile_id} className="min-w-0">
                  <div className="flex items-center justify-between gap-sm">
                    <Link
                      to={missed.kind === "transfer_portal" ? "/transfer-portal" : `/sources/${missed.profile_id}`}
                      className="text-body-sm-medium text-ink"
                    >
                      {missed.name}
                    </Link>
                    <span className="shrink-0 text-caption text-muted">
                      due {formatRelative(missed.expected_at).toLowerCase()}
                    </span>
                  </div>
                  <div className="mt-xxs text-body-sm text-warning-ink">
                    It was due to run and didn't, most likely because the portal was offline at the time.
                  </div>
                  <div className="mt-xxs text-caption text-muted">
                    Missed runs are not made up later. Start it by hand from its page, or it will run at its next
                    scheduled time ({missed.cron} UTC).
                  </div>
                </li>
              ))}
            </AttentionGroup>
          )}

          {data.sign_in_problems.length > 0 && (
            <AttentionGroup title="Sign-in not working">
              {data.sign_in_problems.map((problem) => (
                <li key={problem.credential_id} className="min-w-0">
                  <div className="flex items-center justify-between gap-sm">
                    <span className="text-body-sm-medium text-ink">{problem.name}</span>
                    <span className="shrink-0 text-caption text-muted">
                      {formatRelative(problem.updated_at)}
                    </span>
                  </div>
                  <div className="mt-xxs text-body-sm text-warning-ink">
                    {problem.message ?? "The last sign-in attempt failed."}
                  </div>
                  <div className="mt-xxs text-caption text-muted">
                    Their next run will fail too. Update the sign-in details on the source's page. Used by{" "}
                    {problem.scrapers.map((scraper, index) => (
                      <span key={scraper.profile_id}>
                        {index > 0 && ", "}
                        <Link to={`/sources/${scraper.profile_id}`}>{scraper.name}</Link>
                      </span>
                    ))}
                    .
                  </div>
                </li>
              ))}
            </AttentionGroup>
          )}

          {stuck.length > 0 && (
            <AttentionGroup title="Looks stuck">
              {stuck.map((run) => (
                <li key={run.run_id} className="min-w-0">
                  <Link to={`/runs/${run.run_id}`} className="text-body-sm-medium text-ink">
                    {run.name}
                  </Link>
                  <div className="mt-xxs text-body-sm text-warning-ink">{stuckSentence(run)}</div>
                  <div className="mt-xxs text-caption text-muted">
                    Open the run to see where it stopped, and cancel it if it isn't moving.
                  </div>
                </li>
              ))}
            </AttentionGroup>
          )}
        </div>
      )}
    </Card>
  );
}

function AttentionGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <Label>{title}</Label>
      <ul className="m-0 mt-sm flex list-none flex-col gap-md p-0">{children}</ul>
    </section>
  );
}

// --- Coming up and why runs failed ------------------------------------------

function ComingUp({ data }: { data: DashboardData }) {
  const scheduled = data.scrapers.scheduled + data.transfer_portal.scheduled;
  return (
    <Card title="Coming up" description="The next scrapers due to run on their schedule, in your time.">
      {data.upcoming.length === 0 ? (
        <Muted>No scraper is on a schedule, so every run is started by hand.</Muted>
      ) : (
        <>
          <ul className="m-0 flex list-none flex-col gap-sm p-0">
            {data.upcoming.map((next) => (
              <li key={next.profile_id} className="flex items-start justify-between gap-sm">
                <div className="min-w-0">
                  <Link to={scraperPath(next)} className="text-body-sm-medium text-ink">
                    {next.name}
                  </Link>
                  <div className="text-caption text-muted">{sportName(next.sport)}</div>
                </div>
                <span className="shrink-0 text-right text-body-sm text-ink">
                  {whenPhrase(next.next_run_at, data.timezone)}
                </span>
              </li>
            ))}
          </ul>
          {scheduled > data.upcoming.length && (
            <div className="mt-md text-caption text-muted-soft">
              The next {data.upcoming.length} of {scheduled} scrapers on a schedule.
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * The window's failed runs grouped by cause, most frequent first. Several
 * failures with one cause usually mean one thing to fix, which a count of
 * failures alone cannot tell anybody.
 */
function MostCommonFailures({ data }: { data: DashboardData }) {
  return (
    <Card
      title="Why runs failed"
      description={`Failed runs in ${windowPhrase(data.days)}, grouped by cause. One fix often clears several.`}
    >
      {data.failure_reasons.length === 0 ? (
        <Muted>No runs failed in {windowPhrase(data.days)}.</Muted>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-md p-0">
          {data.failure_reasons.map((reason) => (
            <li key={reason.code ?? "unclassified"} className="min-w-0">
              <div className="flex items-center justify-between gap-sm">
                <span className="text-body-sm-medium text-ink">{reasonTitle(reason.code)}</span>
                <Badge tone="danger">{reasonScale(reason.runs, reason.scrapers.length)}</Badge>
              </div>
              <div className="mt-xxs text-body-sm text-ink">{reason.message}</div>
              {reason.hint && <div className="mt-xxs text-caption text-muted">{reason.hint}</div>}
              <div className="mt-xxs text-caption text-muted">
                {reason.scrapers.map((scraper, index) => (
                  <span key={scraper.profile_id}>
                    {index > 0 && ", "}
                    <Link to={scraperPath(scraper)}>{scraper.name}</Link>
                  </span>
                ))}{" "}
                · most recently{" "}
                <Link to={`/runs/${reason.latest_run_id}`}>{formatRelative(reason.latest_at).toLowerCase()}</Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// --- By sport --------------------------------------------------------------

function BySport({ data }: { data: DashboardData }) {
  return (
    <Card
      title="By sport"
      description={`Scrapers and leagues in each sport, and how their runs went in ${windowPhrase(
        data.days,
      )}. Select a sport to see its scrapers.`}
      flush
    >
      <TableScroll stickyHeader>
        <table className="table">
          <thead>
            <tr>
              <th>Sport</th>
              <th className="text-right">Scrapers</th>
              <th className="text-right">Leagues</th>
              <th className="text-right">Runs worked</th>
              <th className="text-right">Runs failed</th>
              <th className="text-right">Success rate</th>
              <th className="text-right">Players</th>
            </tr>
          </thead>
          <tbody>
            {data.sports.map((sport) => (
              <tr key={sport.sport}>
                <td>
                  <Link
                    to={sourcesForSport(sport.sport)}
                    className="text-body-sm-medium text-ink"
                    title={`See the ${sport.sport} scrapers`}
                  >
                    {sportName(sport.sport)}
                  </Link>
                  {sport.failing > 0 && (
                    <div className="mt-xxs">
                      <Badge tone="danger">{sport.failing} needing attention</Badge>
                    </div>
                  )}
                </td>
                <td className="text-right">{sport.scrapers}</td>
                <td className="text-right">{sport.leagues}</td>
                <td className="text-right">{sport.succeeded}</td>
                <td className={cx("text-right", sport.failed > 0 && "text-danger-ink")}>{sport.failed}</td>
                <td className="text-right">{formatRate(sport.success_rate)}</td>
                <td className="text-right text-body-sm-medium text-ink">{sport.players.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

function TransferPortalSummary({ data }: { data: DashboardData }) {
  const tp = data.transfer_portal;
  return (
    <Card
      title="Transfer Portal"
      description="Scrapers that track players entering the transfer portal, counted separately from the statistics above."
      actions={
        <Link to="/transfer-portal" className="text-body-sm-medium">
          Open
        </Link>
      }
    >
      <div className="text-body-sm text-ink">
        {tp.total} scraper{tp.total === 1 ? "" : "s"} · {tp.scheduled} on a schedule ·{" "}
        <span className={cx(tp.failing > 0 && "text-danger-ink")}>{tp.failing} needing attention</span> ·{" "}
        {tp.succeeded} run{tp.succeeded === 1 ? "" : "s"} worked and {tp.failed} failed in {windowPhrase(data.days)}
      </div>
    </Card>
  );
}

// --- Run history -------------------------------------------------------------

/**
 * Every finished run, newest first, a page at a time.
 *
 * Runs are kept for as long as their scraper exists, so this is paged rather
 * than cut off at the latest few. It fetches its own pages: turning one should
 * not recount the rest of the screen. The previous page stays on screen while
 * the next loads, so the table does not collapse and jump.
 */
function RunHistory() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);

  const { data, error, isPlaceholderData } = useQuery({
    queryKey: keys.dashboardActivity(page, pageSize),
    queryFn: () => api.dashboardActivity(page, pageSize),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_LIST_MS,
  });

  // The history can shrink under a reader — deleting a scraper deletes its
  // runs — and a page past the end would show an empty table.
  const total = data?.total ?? 0;
  const lastPage = pageCount(total, pageSize);
  useEffect(() => {
    if (data && page > lastPage) setPage(clampPage(page, total, pageSize));
  }, [data, page, lastPage, total, pageSize]);

  const runs = data?.items ?? [];

  return (
    <Card
      title="Run history"
      description="Every run that has finished, newest first. Nothing is deleted while its scraper exists."
      flush
    >
      {error && !data ? (
        <div className="px-lg pb-lg">
          <Banner tone="danger" title="The run history could not be loaded.">
            {error.message}
          </Banner>
        </div>
      ) : !data ? (
        <Spinner label="Loading the run history" />
      ) : runs.length === 0 && total === 0 ? (
        <div className="px-lg pb-lg">
          <Muted>No runs have finished yet.</Muted>
        </div>
      ) : (
        <>
          <TableScroll stickyHeader>
            <table className={cx("table", isPlaceholderData && "opacity-60")}>
              <thead>
                <tr>
                  <th>Scraper</th>
                  <th>Sport</th>
                  <th>Result</th>
                  <th>What happened</th>
                  <th>Started</th>
                  <th className="text-right">Finished</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.run_id}>
                    <td>
                      <Link to={scraperPath(run)} className="text-body-sm-medium text-ink">
                        {run.name}
                      </Link>
                    </td>
                    <td>{sportName(run.sport)}</td>
                    <td>
                      <Link to={`/runs/${run.run_id}`} className="no-underline hover:no-underline">
                        <StatusBadge status={run.status} />
                      </Link>
                    </td>
                    <td className={cx("[overflow-wrap:anywhere]", run.status === "failed" && "text-danger-ink")}>
                      {outcomeSentence(run)}
                    </td>
                    <td>
                      <Muted>{triggerLabel(run.trigger)}</Muted>
                    </td>
                    <td className="whitespace-nowrap text-right">
                      <Muted>{formatRelative(run.finished_at)}</Muted>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
          <Pager
            page={Math.min(page, lastPage)}
            pageSize={pageSize}
            total={total}
            lastPage={lastPage}
            busy={isPlaceholderData}
            onPage={setPage}
            onPageSize={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </>
      )}
    </Card>
  );
}
